import { lazy } from "react";
import { RouteObject } from "react-router-dom";

import ClientContainer from "@/clients/components/ClientContainer/ClientContainer";
import ClientDetail from "@/clients/components/ClientDetail/ClientDetail";
import ClientLabFlowSheet from "@/clients/components/ClientLabFlowSheet";
import AmendEncounter from "@/encounters/components/AmendEncounter/AmendEncounter";
import EncounterLaborAmend from "@/encounters/components/EncounterLabor/EncounterLaborAmend/EncounterLaborAmend";
import LaborVersionDiff from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/LaborVersionDiff";
import GynecologicalVersionDiff from "@/encounters/components/GynecologicalEncounter/components/versionDiff/GynecologicalVersionDiff";
import VersionDiff from "@/encounters/components/NewbornIPPEncounter/components/VersionDiff";
import NewbornPPVersionDiff from "@/encounters/components/NewbornPostpartumEncounter/components/VersionDiff";
import PostpartumVersionDiff from "@/encounters/components/PostpartumEncounter/components/versionDiff";
import ProviderAmendmentGate from "@/encounters/components/ProviderAmendmentGate";
import TransferOfCareVersionDiff from "@/encounters/components/TransferOfCareEncounter/components/TransferOfCareVersionDiff";
const Chat = lazy(() => import("@/messages/Chat"));
const ClientMessages = lazy(() => import("@/messages/components/ClientMessages/ClientMessages"));
const Messages = lazy(() => import("@/messages/Messages"));
const FileUploads = lazy(() => import("@/file-uploads"));
const EncountersTabPanel = lazy(() => import("@/encounters/components/EncountersTabPanel"));
const EncounterBase = lazy(() => import("@/encounters/components/EncounterForms/EncounterBase"));
const AmendEncounterDetails = lazy(() => import("@/encounters/components/AmendEncounterDetails"));
const ProblemsAllergiesMedsTabPanel = lazy(
  () => import("@/problems-allergies-meds/components/ProblemsAllergiesMedsTabPanel"),
);
const ClientHistoryPanel = lazy(() => import("@/clients/components/ClientHistoryPanel"));
const ProceduresPreviousVersions = lazy(
  () => import("@/encounters/components/ProceduresPreviousVersions"),
);

const AssessmentsAndVitalsPanel = lazy(() =>
  import("@/assessments-and-vitals/AssessmentsAndVitalsPanel").then((module) => ({
    default: module.AssessmentsAndVitalsPanel,
  })),
);
const Profile = lazy(() => import("@/profile"));
const Billing = lazy(() => import("@/billing"));
const IntakeForms = lazy(() => import("@/intake-forms/IntakeForms"));
const Consents = lazy(() => import("@/consents/Consents"));
const Assessments = lazy(() => import("@/assessments/Assessments"));
const QuestionnaireClientForm = lazy(() =>
  import("@/clients/components/QuestionnaireClientForm").then((module) => ({
    default: module.QuestionnaireClientForm,
  })),
);
const ConsentQuestionnaireClientForm = lazy(() =>
  import("@/clients/components/QuestionnaireClientForm").then((module) => ({
    default: module.ConsentQuestionnaireClientForm,
  })),
);
const EpisodeOfCare = lazy(() => import("@/episode-of-care"));
const EpisodeOfCareEncounterList = lazy(
  () => import("@/episode-of-care/components/EpisodeOfCareEncounterList"),
);
const SignUpForm = lazy(() => import("@/sign-up/components/SignUpForm"));
const SetPassword = lazy(() => import("@/set-password/SetPassword"));
const ClientListRedirect = lazy(() => import("@/clients/components/ClientListRedirect"));
const Overview = lazy(() => import("@/clients/components/Overview"));
const ClientHistoryQuestionnaireResponsePreviousVersions = lazy(
  () => import("@/clients/components/ClientHistoryQuestionnaireResponsePreviousVersions"),
);
const StatementDetail = lazy(() => import("@/billing/components/StatementDetail/StatementDetail"));
const LedgerPaymentHistory = lazy(
  () => import("@/billing/components/LedgerPaymentHistory/LedgerPaymentHistory"),
);
const StatementStatusHistory = lazy(
  () => import("@/billing/components/StatementStatusHistory/StatementStatusHistory"),
);
const ChargeItemHistory = lazy(
  () => import("@/billing/components/ChargeItemHistory/ChargeItemHistory"),
);
const BillingQuestionnaireResponsePreviousVersions = lazy(
  () => import("@/billing/components/BillingQuestionnaireResponsePreviousVersions"),
);
const VerifyInsurance = lazy(() => import("@/billing/components/VerifyInsurance/VerifyInsurance"));
const AssessmentWrapper = lazy(
  () =>
    import(
      "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/AssessmentWrapper"
    ),
);
const FullLaborAssessment = lazy(
  () =>
    import(
      "@/encounters/components/EncounterLabor/EncounterLabor/LaborFlowsheetComponents/FullLaborAssessment"
    ),
);
const FullNewbornAssessment = lazy(
  () =>
    import(
      "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/FullNewbornAssessment"
    ),
);
const ClientFullIppAssessment = lazy(
  () =>
    import(
      "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/ClientFullIppAssessment"
    ),
);
const ClientResultsPanel = lazy(() => import("@/clients/components/ClientResultsPanel"));

const ResetPassword = lazy(() => import("@/reset-password/ResetPassword"));

const EduResourcesPage = lazy(() =>
  import("@/edu-resources/components/EduResourcesPage").then((module) => ({
    default: module.EduResourcesPage,
  })),
);

const ResourceContent = lazy(() =>
  import("@/edu-resources/components/ResourceContent").then((module) => ({
    default: module.ResourceContent,
  })),
);

export const ClientRoutes: RouteObject[] = [
  {
    path: "/clients",
    element: <ClientListRedirect />,
  },
  {
    path: "/clients/:clientId/register/:projectId",
    element: <SignUpForm />,
  },
  {
    path: "/clients/:clientId/set-password/:id/:secret",
    element: <SetPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/clients/:clientId/results/flow-sheet",
    element: <ClientLabFlowSheet />,
  },
  {
    path: "/clients/:clientId",
    element: <ClientContainer />,
    children: [
      {
        path: "intake",
        element: <IntakeForms />,
      },
      {
        path: "consents",
        element: <Consents />,
      },
      {
        path: "assessments",
        element: <Assessments />,
      },
      {
        path: "edu-resources",
        element: <EduResourcesPage />,
      },
      {
        path: "edu-resources/:resourceId",
        element: <ResourceContent />,
      },
      {
        path: "questionnaires/:questionnaireId",
        element: <QuestionnaireClientForm />,
      },
      {
        path: "episode-of-care/:tabValue",
        element: <EpisodeOfCare />,
        children: [
          {
            path: ":episodeId",
            element: <EpisodeOfCareEncounterList />,
          },
        ],
      },
      {
        path: "consents/:consentQuestionnaireId",
        element: <ConsentQuestionnaireClientForm />,
      },
      {
        path: "encounters/:encounterId/labor-assessment",
        element: <AssessmentWrapper AssessmentComponent={FullLaborAssessment} />,
      },
      {
        path: "encounters/:encounterId/newborn-ipp-assessment",
        element: <AssessmentWrapper AssessmentComponent={FullNewbornAssessment} />,
      },
      {
        path: "encounters/:encounterId/client-ipp-assessment",
        element: <AssessmentWrapper AssessmentComponent={ClientFullIppAssessment} />,
      },
      {
        path: "billing/verify/:tier",
        element: <VerifyInsurance />,
      },
      {
        path: "billing/statements/:invoiceId",
        element: <StatementDetail />,
      },
      {
        path: "billing/statements/:invoiceId/history",
        element: <StatementStatusHistory />,
      },
      {
        path: "billing/ledger/:invoiceId/history",
        element: <LedgerPaymentHistory />,
      },
      {
        path: "billing/services/:chargeItemId/history",
        element: <ChargeItemHistory />,
      },
      {
        path: "",
        element: <ClientDetail />,
        children: [
          {
            index: true,
            element: <Overview />,
          },
          {
            path: "overview",
            element: <Overview />,
          },
          {
            path: "encounters",
            element: <EncountersTabPanel />,
            children: [
              {
                path: ":encounterId",
                element: <EncounterBase />,
              },
              {
                path: ":encounterId/newborn-ipp/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterBase />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/newborn-ipp/amend/:versionId",
                element: <VersionDiff />,
              },
              {
                path: ":encounterId/postpartum/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterBase />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/postpartum/amend/:versionId",
                element: <PostpartumVersionDiff />,
              },
              {
                path: ":encounterId/newborn-postpartum/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterBase />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/newborn-postpartum/amend/:versionId",
                element: <NewbornPPVersionDiff />,
              },
              {
                path: ":encounterId/gynecological/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterBase />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/gynecological/amend/:versionId",
                element: <GynecologicalVersionDiff />,
              },
              {
                path: ":encounterId/amend",
                element: (
                  <ProviderAmendmentGate>
                    <AmendEncounter />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/amend/:versionId",
                element: <AmendEncounterDetails />,
              },
              {
                path: ":encounterId/labor/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterLaborAmend />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/labor/amend/:versionId",
                element: <LaborVersionDiff />,
              },
              {
                path: ":encounterId/transfer-of-care/amend",
                element: (
                  <ProviderAmendmentGate>
                    <EncounterBase />
                  </ProviderAmendmentGate>
                ),
              },
              {
                path: ":encounterId/transfer-of-care/amend/:versionId",
                element: <TransferOfCareVersionDiff />,
              },
            ],
          },
          {
            path: "pam",
            element: <ProblemsAllergiesMedsTabPanel />,
          },
          {
            path: "history",
            element: <ClientHistoryPanel />,
          },
          {
            path: "history/versions/:path",
            element: <ClientHistoryQuestionnaireResponsePreviousVersions />,
          },
          {
            path: "procedures/version/:questionnaireResponseId",
            element: <ProceduresPreviousVersions />,
          },
          {
            path: "assessments-vitals/:tabValue/:questionnaireResponseId?",
            element: <AssessmentsAndVitalsPanel />,
          },
          {
            path: "profile",
            element: <Profile />,
          },
          {
            path: "results",
            element: <ClientResultsPanel />,
          },
          {
            path: "referrals",
            element: null, // TODO: Add component
          },
          {
            path: "messages/:communicationId?",
            element: <ClientMessages />,
          },
          {
            path: "files",
            element: <FileUploads />,
          },
          {
            path: "billing",
            element: <Billing />,
          },
          {
            path: "billing/versions/:section",
            element: <BillingQuestionnaireResponsePreviousVersions />,
          },
        ],
      },
    ],
  },
  {
    path: "/messages",
    element: <Messages />,
    children: [
      {
        path: ":communicationId",
        element: <Chat />,
      },
    ],
  },
];
